import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import { parseThemeApiResponse, siteThemeAdminUrl } from "../ThemeManagement/themeApiUtils";

const AppearanceCustomize = () => {
  const urlmain = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const [payload, setPayload] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(siteThemeAdminUrl("/settings"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        setPayload(res.data);
        setSettings(res.data?.settings || {});
      } else {
        toast.error(res.message || "No active theme");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(siteThemeAdminUrl("/settings"), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ settings }),
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        toast.success("Settings saved");
        setPayload(res.data);
        setSettings(res.data?.settings || {});
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (key, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("setting_key", key);
    body.append("file", file);
    try {
      const response = await fetch(siteThemeAdminUrl("/settings/upload"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        handleChange(key, res.data.url);
        toast.success("Image uploaded");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const assetUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${urlmain}${path}`;
  };

  const renderField = (field) => {
    const value = settings[field.key];

    if (field.type === "toggle") {
      return (
        <label key={field.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => handleChange(field.key, e.target.checked)}
          />
          {field.label}
        </label>
      );
    }

    if (field.type === "color") {
      return (
        <div key={field.key} style={{ marginBottom: 12 }}>
          <label>{field.label}</label>
          <input
            type="color"
            value={value || field.default || "#000000"}
            onChange={(e) => handleChange(field.key, e.target.value)}
            style={{ display: "block", width: 80, height: 36 }}
          />
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <div key={field.key} style={{ marginBottom: 12 }}>
          <label>{field.label}</label>
          <select
            value={value || field.default || ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
            style={{ width: "100%", maxWidth: 320 }}
          >
            {(field.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === "text") {
      return (
        <div key={field.key} style={{ marginBottom: 12 }}>
          <label>{field.label}</label>
          <input
            type="text"
            value={value ?? field.default ?? ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
            style={{ width: "100%", maxWidth: 480, padding: 8 }}
          />
        </div>
      );
    }

    if (field.type === "image") {
      return (
        <div key={field.key} style={{ marginBottom: 12 }}>
          <label>{field.label}</label>
          {value && (
            <img
              src={assetUrl(value)}
              alt={field.label}
              style={{ display: "block", maxWidth: 120, marginBottom: 8, borderRadius: 6 }}
            />
          )}
          <input type="file" accept="image/*" onChange={(e) => handleImageUpload(field.key, e.target.files?.[0])} />
        </div>
      );
    }

    return null;
  };

  if (loading) return <p>Loading customize settings...</p>;

  if (!payload) {
    return (
      <div>
        <h1 className="dashboard-title">Customize</h1>
        <p>No active theme. Install and activate a theme first.</p>
      </div>
    );
  }

  return (
    <>
      <div className="oms-header">
        <h1 className="dashboard-title">Customize — {payload.theme?.name}</h1>
        <button className="oms-add-btn" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {(payload.configSchema?.groups || []).map((group) => (
        <div
          key={group.key}
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 20,
            marginBottom: 16,
          }}
        >
          <h3 style={{ marginTop: 0 }}>{group.label}</h3>
          {group.description && (
            <p style={{ fontSize: 13, color: "#666", marginTop: -8, marginBottom: 16 }}>{group.description}</p>
          )}
          {(group.fields || []).map((field) => renderField(field))}
        </div>
      ))}
    </>
  );
};

export default AppearanceCustomize;
